import React, { useEffect, useState, useMemo } from 'react';
import api from '../api';
import { toast } from 'sonner';
import { 
  Plus, Wrench, Package, CalendarCheck, Buildings, 
  MagnifyingGlass, PencilSimple, Trash, Eye, Check, X,
  Warning, CheckCircle, Clock, ArrowRight, User, WhatsappLogo
} from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const Facilities = ({ user }) => {
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(null);
  const [activeTab, setActiveTab] = useState('work-orders');
  
  // Data states
  const [dashboardStats, setDashboardStats] = useState({});
  const [assets, setAssets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [locations, setLocations] = useState([]);
  const [workOrders, setWorkOrders] = useState([]);
  const [pmSchedules, setPmSchedules] = useState([]);
  const [parts, setParts] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  
  // Selection states
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [selectedWorkOrder, setSelectedWorkOrder] = useState(null);
  const [selectedPart, setSelectedPart] = useState(null);
  const [selectedTechnician, setSelectedTechnician] = useState(null);
  const [selectedPMSchedule, setSelectedPMSchedule] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  
  // Filter states
  const [assetFilter, setAssetFilter] = useState({ category: '', status: '' });
  const [woFilter, setWoFilter] = useState({ status: '', priority: '' });
  const [partSearch, setPartSearch] = useState('');

  // Role-based access - isAdmin includes manager role
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'manager';
  const isRegularUser = !isAdmin;

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      // Regular users only need work orders and locations (for creating work orders)
      if (isRegularUser) {
        const [dashRes, locRes, woRes] = await Promise.all([
          api.get('/facilities/dashboard'),
          api.get('/facilities/locations'),
          api.get('/facilities/work-orders')
        ]);
        setDashboardStats(dashRes.data);
        setLocations(locRes.data);
        setWorkOrders(woRes.data);
      } else {
        // Admins/Managers get full data
        const [
          dashRes, assetsRes, catRes, locRes, woRes, pmRes, partsRes, techRes
        ] = await Promise.all([
          api.get('/facilities/dashboard'),
          api.get('/facilities/assets'),
          api.get('/facilities/categories'),
          api.get('/facilities/locations'),
          api.get('/facilities/work-orders'),
          api.get('/facilities/pm-schedules'),
          api.get('/facilities/parts'),
          api.get('/facilities/technicians')
        ]);
        
        setDashboardStats(dashRes.data);
        setAssets(assetsRes.data);
        setCategories(catRes.data);
        setLocations(locRes.data);
        setWorkOrders(woRes.data);
        setPmSchedules(pmRes.data);
        setParts(partsRes.data);
        setTechnicians(techRes.data);
      }
    } catch (error) {
      toast.error('Failed to load facilities data');
    } finally {
      setLoading(false);
    }
  };

  // Filtered data
  const filteredAssets = useMemo(() => {
    return assets.filter(a => {
      const matchesCat = !assetFilter.category || a.category_id === assetFilter.category;
      const matchesStatus = !assetFilter.status || a.status === assetFilter.status;
      return matchesCat && matchesStatus;
    });
  }, [assets, assetFilter]);

  const filteredWorkOrders = useMemo(() => {
    return workOrders.filter(wo => {
      const matchesStatus = !woFilter.status || wo.status === woFilter.status;
      const matchesPriority = !woFilter.priority || wo.priority === woFilter.priority;
      return matchesStatus && matchesPriority;
    });
  }, [workOrders, woFilter]);

  const filteredParts = useMemo(() => {
    if (!partSearch) return parts;
    const search = partSearch.toLowerCase();
    return parts.filter(p => 
      p.name.toLowerCase().includes(search) || 
      p.sku.toLowerCase().includes(search)
    );
  }, [parts, partSearch]);

  // Handlers
  const handleCreateCategory = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    try {
      await api.post(`/facilities/categories?name=${encodeURIComponent(formData.get('name'))}&description=${encodeURIComponent(formData.get('description') || '')}`);
      toast.success('Category created');
      setOpenDialog(null);
      loadData();
    } catch (error) {
      toast.error('Failed to create category');
    }
  };

  const handleCreateLocation = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    try {
      await api.post('/facilities/locations', {
        name: formData.get('name'),
        building: formData.get('building'),
        floor: formData.get('floor'),
        description: formData.get('description')
      });
      toast.success('Location created');
      setOpenDialog(null);
      loadData();
    } catch (error) {
      toast.error('Failed to create location');
    }
  };

  const handleCreateAsset = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    try {
      await api.post('/facilities/assets', {
        name: formData.get('name'),
        asset_tag: formData.get('asset_tag'),
        category_id: formData.get('category_id'),
        location_id: formData.get('location_id'),
        status: formData.get('status') || 'active',
        purchase_date: formData.get('purchase_date') || null,
        purchase_cost: formData.get('purchase_cost') ? parseFloat(formData.get('purchase_cost')) : null,
        serial_number: formData.get('serial_number') || null,
        warranty_expiry: formData.get('warranty_expiry') || null,
        notes: formData.get('notes') || null
      });
      toast.success('Asset created');
      setOpenDialog(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create asset');
    }
  };

  const handleUpdateAsset = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    try {
      await api.put(`/facilities/assets/${editingItem.id}`, {
        name: formData.get('name'),
        asset_tag: formData.get('asset_tag'),
        category_id: formData.get('category_id'),
        location_id: formData.get('location_id'),
        status: formData.get('status'),
        purchase_date: formData.get('purchase_date') || null,
        purchase_cost: formData.get('purchase_cost') ? parseFloat(formData.get('purchase_cost')) : null,
        serial_number: formData.get('serial_number') || null,
        warranty_expiry: formData.get('warranty_expiry') || null,
        notes: formData.get('notes') || null
      });
      toast.success('Asset updated');
      setOpenDialog(null);
      setEditingItem(null);
      loadData();
    } catch (error) {
      toast.error('Failed to update asset');
    }
  };

  const handleDeleteAsset = async (assetId) => {
    if (!window.confirm('Delete this asset?')) return;
    try {
      await api.delete(`/facilities/assets/${assetId}`);
      toast.success('Asset deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete asset');
    }
  };

  const handleCreateWorkOrder = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    try {
      await api.post('/facilities/work-orders', {
        title: formData.get('title'),
        description: formData.get('description'),
        asset_id: formData.get('asset_id') || null,
        priority: formData.get('priority'),
        requested_by: user.id,
        location_id: formData.get('location_id') || null
      });
      toast.success('Work order created');
      setOpenDialog(null);
      loadData();
    } catch (error) {
      toast.error('Failed to create work order');
    }
  };

  const handleUpdateWorkOrder = async (workOrderId, updates) => {
    try {
      await api.put(`/facilities/work-orders/${workOrderId}`, updates);
      toast.success('Work order updated');
      loadData();
    } catch (error) {
      toast.error('Failed to update work order');
    }
  };

  const handleCreatePMSchedule = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    try {
      await api.post('/facilities/pm-schedules', {
        name: formData.get('name'),
        asset_id: formData.get('asset_id'),
        frequency: formData.get('frequency'),
        description: formData.get('description'),
        next_due_date: formData.get('next_due_date')
      });
      toast.success('PM schedule created');
      setOpenDialog(null);
      loadData();
    } catch (error) {
      toast.error('Failed to create PM schedule');
    }
  };

  const handleCompletePM = async (scheduleId) => {
    try {
      await api.put(`/facilities/pm-schedules/${scheduleId}/complete`);
      toast.success('PM completed');
      loadData();
    } catch (error) {
      toast.error('Failed to complete PM');
    }
  };

  const handleCreatePart = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    try {
      await api.post('/facilities/parts', {
        name: formData.get('name'),
        sku: formData.get('sku'),
        category: formData.get('category'),
        quantity: parseInt(formData.get('quantity')) || 0,
        min_quantity: parseInt(formData.get('min_quantity')) || 0,
        unit_cost: parseFloat(formData.get('unit_cost')) || 0,
        location: formData.get('location') || null,
        vendor: formData.get('vendor') || null
      });
      toast.success('Part added');
      setOpenDialog(null);
      loadData();
    } catch (error) {
      toast.error('Failed to add part');
    }
  };

  const handleAdjustStock = async (partId, adjustment) => {
    try {
      await api.put(`/facilities/parts/${partId}/adjust-stock`, { quantity: adjustment });
      toast.success('Stock adjusted');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to adjust stock');
    }
  };

  // Technician handlers
  const handleCreateTechnician = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    try {
      await api.post('/facilities/technicians', {
        name: formData.get('name'),
        phone: formData.get('phone'),
        specialization: formData.get('specialization'),
        status: formData.get('status') || 'available'
      });
      toast.success('Technician added');
      setOpenDialog(null);
      loadData();
    } catch (error) {
      toast.error('Failed to add technician');
    }
  };

  const handleUpdateTechnician = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    try {
      await api.put(`/facilities/technicians/${editingItem.id}`, {
        name: formData.get('name'),
        phone: formData.get('phone'),
        specialization: formData.get('specialization'),
        status: formData.get('status')
      });
      toast.success('Technician updated');
      setOpenDialog(null);
      setEditingItem(null);
      loadData();
    } catch (error) {
      toast.error('Failed to update technician');
    }
  };

  const handleDeleteTechnician = async (techId) => {
    if (!window.confirm('Delete this technician?')) return;
    try {
      await api.delete(`/facilities/technicians/${techId}`);
      toast.success('Technician deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete technician');
    }
  };

  const handleReassignWorkOrder = async (workOrderId, technicianId) => {
    try {
      await api.put(`/facilities/work-orders/${workOrderId}`, { assigned_to: technicianId });
      toast.success('Work order reassigned');
      loadData();
      setOpenDialog(null);
      setSelectedWorkOrder(null);
    } catch (error) {
      toast.error('Failed to reassign work order');
    }
  };

  // WhatsApp notification helper
  const openWhatsApp = (phone, workOrder) => {
    // Clean phone number (remove spaces, dashes)
    const cleanPhone = phone.replace(/[\s-]/g, '');
    // Create message
    const message = encodeURIComponent(
      `Hi! You have been assigned a new work order:\n\n` +
      `Title: ${workOrder.title}\n` +
      `Priority: ${workOrder.priority.toUpperCase()}\n` +
      `Location: ${workOrder.location_name || 'Not specified'}\n` +
      `Asset: ${workOrder.asset_name || 'Not specified'}\n\n` +
      `Description: ${workOrder.description}\n\n` +
      `Please acknowledge and start working on this task.`
    );
    window.open(`https://wa.me/${cleanPhone}?text=${message}`, '_blank');
  };

  const getStatusColor = (status) => {
    const colors = {
      active: 'bg-emerald-100 text-emerald-800',
      down: 'bg-red-100 text-red-800',
      maintenance: 'bg-amber-100 text-amber-800',
      retired: 'bg-gray-100 text-gray-800',
      new: 'bg-blue-100 text-blue-800',
      assigned: 'bg-purple-100 text-purple-800',
      in_progress: 'bg-amber-100 text-amber-800',
      on_hold: 'bg-orange-100 text-orange-800',
      completed: 'bg-emerald-100 text-emerald-800',
      closed: 'bg-gray-100 text-gray-800',
      in_stock: 'bg-emerald-100 text-emerald-800',
      low_stock: 'bg-amber-100 text-amber-800',
      out_of_stock: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      critical: 'bg-red-100 text-red-800 border-red-300',
      high: 'bg-orange-100 text-orange-800 border-orange-300',
      medium: 'bg-amber-100 text-amber-800 border-amber-300',
      low: 'bg-green-100 text-green-800 border-green-300'
    };
    return colors[priority] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div data-testid="facilities-page">
      <div className="mb-8">
        <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-[#2B3210] mb-2">
          Facilities Management
        </h1>
        <p className="text-base text-[#505631]">
          {isRegularUser 
            ? 'Submit and track your maintenance work orders' 
            : 'Manage assets, work orders, maintenance, and inventory'}
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-[#FBF8EF] border border-[#E5E2D9] flex flex-wrap gap-1 h-auto p-1">
          {isAdmin && (
            <TabsTrigger value="dashboard" className="data-[state=active]:bg-white text-xs sm:text-sm">
              <Buildings size={16} className="mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Dashboard</span>
              <span className="sm:hidden">Dash</span>
            </TabsTrigger>
          )}
          {isAdmin && (
            <TabsTrigger value="assets" className="data-[state=active]:bg-white text-xs sm:text-sm">
              <Buildings size={16} className="mr-1 sm:mr-2" />
              Assets
            </TabsTrigger>
          )}
          <TabsTrigger value="work-orders" className="data-[state=active]:bg-white text-xs sm:text-sm">
            <Wrench size={16} className="mr-1 sm:mr-2" />
            <span className="hidden sm:inline">{isRegularUser ? 'My Work Orders' : 'Work Orders'}</span>
            <span className="sm:hidden">Orders</span>
          </TabsTrigger>
          {isAdmin && (
            <TabsTrigger value="technicians" className="data-[state=active]:bg-white text-xs sm:text-sm">
              <User size={16} className="mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Technicians</span>
              <span className="sm:hidden">Techs</span>
            </TabsTrigger>
          )}
          {isAdmin && (
            <TabsTrigger value="pm" className="data-[state=active]:bg-white text-xs sm:text-sm">
              <CalendarCheck size={16} className="mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Preventive Maintenance</span>
              <span className="sm:hidden">PM</span>
            </TabsTrigger>
          )}
          {isAdmin && (
            <TabsTrigger value="inventory" className="data-[state=active]:bg-white text-xs sm:text-sm">
              <Package size={16} className="mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Inventory</span>
              <span className="sm:hidden">Inv</span>
            </TabsTrigger>
          )}
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-xl border border-[#E5E2D9] p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#505631]">Total Assets</p>
                  <p className="text-3xl font-bold text-[#2B3210]">{dashboardStats.total_assets || 0}</p>
                </div>
                <div className="w-12 h-12 bg-[#B2560D]/10 rounded-lg flex items-center justify-center">
                  <Buildings size={24} className="text-[#B2560D]" />
                </div>
              </div>
              <p className="text-xs text-[#505631] mt-2">
                <span className="text-emerald-600">{dashboardStats.active_assets || 0} active</span>
                {dashboardStats.down_assets > 0 && (
                  <span className="text-red-600 ml-2">{dashboardStats.down_assets} down</span>
                )}
              </p>
            </div>

            <div className="bg-white rounded-xl border border-[#E5E2D9] p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#505631]">Open Work Orders</p>
                  <p className="text-3xl font-bold text-[#2B3210]">{dashboardStats.open_work_orders || 0}</p>
                </div>
                <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                  <Wrench size={24} className="text-amber-600" />
                </div>
              </div>
              {dashboardStats.critical_work_orders > 0 && (
                <p className="text-xs text-red-600 mt-2 flex items-center gap-1">
                  <Warning size={14} /> {dashboardStats.critical_work_orders} critical
                </p>
              )}
            </div>

            <div className="bg-white rounded-xl border border-[#E5E2D9] p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#505631]">Overdue PM</p>
                  <p className={`text-3xl font-bold ${dashboardStats.overdue_pm > 0 ? 'text-red-600' : 'text-[#2B3210]'}`}>
                    {dashboardStats.overdue_pm || 0}
                  </p>
                </div>
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${dashboardStats.overdue_pm > 0 ? 'bg-red-100' : 'bg-emerald-100'}`}>
                  <CalendarCheck size={24} className={dashboardStats.overdue_pm > 0 ? 'text-red-600' : 'text-emerald-600'} />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-[#E5E2D9] p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#505631]">Low Stock Parts</p>
                  <p className={`text-3xl font-bold ${dashboardStats.low_stock_parts > 0 ? 'text-amber-600' : 'text-[#2B3210]'}`}>
                    {dashboardStats.low_stock_parts || 0}
                  </p>
                </div>
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${dashboardStats.low_stock_parts > 0 ? 'bg-amber-100' : 'bg-emerald-100'}`}>
                  <Package size={24} className={dashboardStats.low_stock_parts > 0 ? 'text-amber-600' : 'text-emerald-600'} />
                </div>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-white rounded-xl border border-[#E5E2D9] p-6 mb-6">
            <h2 className="text-lg font-semibold text-[#2B3210] mb-4">Quick Actions</h2>
            <div className="flex flex-wrap gap-3">
              <button onClick={() => { setActiveTab('work-orders'); setOpenDialog('createWorkOrder'); }} className="btn-primary flex items-center gap-2">
                <Plus size={18} /> New Work Order
              </button>
              {isAdmin && (
                <>
                  <button onClick={() => { setActiveTab('assets'); setOpenDialog('createAsset'); }} className="px-4 py-2 border border-[#B2560D] text-[#B2560D] rounded-lg hover:bg-orange-50 flex items-center gap-2">
                    <Plus size={18} /> Add Asset
                  </button>
                  <button onClick={() => { setActiveTab('inventory'); setOpenDialog('createPart'); }} className="px-4 py-2 border border-[#505631] text-[#505631] rounded-lg hover:bg-[#FBF8EF] flex items-center gap-2">
                    <Plus size={18} /> Add Part
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Recent Work Orders */}
          <div className="bg-white rounded-xl border border-[#E5E2D9] p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-[#2B3210]">Recent Work Orders</h2>
              <button onClick={() => setActiveTab('work-orders')} className="text-sm text-[#B2560D] hover:underline flex items-center gap-1">
                View all <ArrowRight size={14} />
              </button>
            </div>
            <div className="space-y-3">
              {workOrders.slice(0, 5).map(wo => (
                <div key={wo.id} className="flex items-center justify-between p-3 bg-[#FBF8EF] rounded-lg">
                  <div>
                    <p className="font-medium text-[#2B3210]">{wo.title}</p>
                    <p className="text-sm text-[#505631]">{wo.asset_name || 'No asset'}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(wo.priority)}`}>
                      {wo.priority}
                    </span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(wo.status)}`}>
                      {wo.status.replace('_', ' ')}
                    </span>
                  </div>
                </div>
              ))}
              {workOrders.length === 0 && (
                <p className="text-center text-[#505631] py-4">No work orders yet</p>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Assets Tab */}
        <TabsContent value="assets">
          <div className="bg-white rounded-xl border border-[#E5E2D9] overflow-hidden">
            <div className="p-6 border-b border-[#E5E2D9] flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <h2 className="text-xl font-medium text-[#2B3210]">Asset Registry</h2>
              <div className="flex flex-wrap gap-3 items-center">
                <select
                  value={assetFilter.category}
                  onChange={(e) => setAssetFilter(f => ({ ...f, category: e.target.value }))}
                  className="px-3 py-2 border border-[#E5E2D9] rounded-lg text-sm"
                >
                  <option value="">All Categories</option>
                  {categories.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
                <select
                  value={assetFilter.status}
                  onChange={(e) => setAssetFilter(f => ({ ...f, status: e.target.value }))}
                  className="px-3 py-2 border border-[#E5E2D9] rounded-lg text-sm"
                >
                  <option value="">All Status</option>
                  <option value="active">Active</option>
                  <option value="down">Down</option>
                  <option value="maintenance">Maintenance</option>
                  <option value="retired">Retired</option>
                </select>
                {isAdmin && (
                  <div className="flex gap-2">
                    <button onClick={() => setOpenDialog('createCategory')} className="px-3 py-2 border border-[#E5E2D9] text-[#505631] rounded-lg hover:bg-[#FBF8EF] text-sm">
                      + Category
                    </button>
                    <button onClick={() => setOpenDialog('createLocation')} className="px-3 py-2 border border-[#E5E2D9] text-[#505631] rounded-lg hover:bg-[#FBF8EF] text-sm">
                      + Location
                    </button>
                    <button onClick={() => setOpenDialog('createAsset')} className="btn-primary flex items-center gap-2" data-testid="create-asset-button">
                      <Plus size={18} /> New Asset
                    </button>
                  </div>
                )}
              </div>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#FBF8EF] border-b border-[#E5E2D9]">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase">Asset Tag</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase">Category</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase">Location</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-200">
                  {filteredAssets.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-6 py-8 text-center text-[#505631]">
                        No assets found. {isAdmin && 'Add one to get started.'}
                      </td>
                    </tr>
                  ) : (
                    filteredAssets.map(asset => (
                      <tr 
                        key={asset.id} 
                        className="hover:bg-[#FBF8EF] cursor-pointer transition-colors"
                        onClick={() => { setSelectedAsset(asset); setOpenDialog('assetDetails'); }}
                      >
                        <td className="px-6 py-4 text-sm font-mono text-[#B2560D]">{asset.asset_tag}</td>
                        <td className="px-6 py-4 text-sm font-medium text-[#2B3210]">{asset.name}</td>
                        <td className="px-6 py-4 text-sm text-[#505631]">{asset.category_name}</td>
                        <td className="px-6 py-4 text-sm text-[#505631]">{asset.location_name}</td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(asset.status)}`}>
                            {asset.status}
                          </span>
                        </td>
                        <td className="px-6 py-4" onClick={e => e.stopPropagation()}>
                          <div className="flex gap-2">
                            {isAdmin && (
                              <>
                                <button
                                  onClick={() => { setEditingItem(asset); setOpenDialog('editAsset'); }}
                                  className="p-2 text-[#B2560D] hover:bg-orange-50 rounded-lg"
                                  title="Edit"
                                >
                                  <PencilSimple size={18} />
                                </button>
                                <button
                                  onClick={() => handleDeleteAsset(asset.id)}
                                  className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                                  title="Delete"
                                >
                                  <Trash size={18} />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        {/* Work Orders Tab */}
        <TabsContent value="work-orders">
          <div className="bg-white rounded-xl border border-[#E5E2D9] overflow-hidden">
            <div className="p-6 border-b border-[#E5E2D9] flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <h2 className="text-xl font-medium text-[#2B3210]">{isRegularUser ? 'My Work Orders' : 'Work Orders'}</h2>
              <div className="flex flex-wrap gap-3 items-center">
                <select
                  value={woFilter.status}
                  onChange={(e) => setWoFilter(f => ({ ...f, status: e.target.value }))}
                  className="px-3 py-2 border border-[#E5E2D9] rounded-lg text-sm"
                >
                  <option value="">All Status</option>
                  <option value="new">New</option>
                  <option value="assigned">Assigned</option>
                  <option value="in_progress">In Progress</option>
                  <option value="on_hold">On Hold</option>
                  <option value="completed">Completed</option>
                  <option value="closed">Closed</option>
                </select>
                <select
                  value={woFilter.priority}
                  onChange={(e) => setWoFilter(f => ({ ...f, priority: e.target.value }))}
                  className="px-3 py-2 border border-[#E5E2D9] rounded-lg text-sm"
                >
                  <option value="">All Priority</option>
                  <option value="critical">Critical</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
                <button onClick={() => setOpenDialog('createWorkOrder')} className="btn-primary flex items-center gap-2" data-testid="create-work-order-button">
                  <Plus size={18} /> New Work Order
                </button>
              </div>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#FBF8EF] border-b border-[#E5E2D9]">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase">Title</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase">Asset</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase">Priority</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase">Assigned To</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-200">
                  {filteredWorkOrders.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-6 py-8 text-center text-[#505631]">
                        No work orders found.
                      </td>
                    </tr>
                  ) : (
                    filteredWorkOrders.map(wo => (
                      <tr 
                        key={wo.id} 
                        className="hover:bg-[#FBF8EF] cursor-pointer transition-colors"
                        onClick={() => { setSelectedWorkOrder(wo); setOpenDialog('workOrderDetails'); }}
                      >
                        <td className="px-6 py-4">
                          <div className="text-sm font-medium text-[#2B3210]">{wo.title}</div>
                          <div className="text-xs text-[#505631]">{wo.description?.substring(0, 50)}...</div>
                        </td>
                        <td className="px-6 py-4 text-sm text-[#505631]">{wo.asset_name || '-'}</td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getPriorityColor(wo.priority)}`}>
                            {wo.priority}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(wo.status)}`}>
                            {wo.status.replace('_', ' ')}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-[#505631]">{wo.assigned_to_name || 'Unassigned'}</td>
                        <td className="px-6 py-4" onClick={e => e.stopPropagation()}>
                          <div className="flex gap-2">
                            {wo.status === 'new' && isAdmin && (
                              <button
                                onClick={() => handleUpdateWorkOrder(wo.id, { status: 'in_progress' })}
                                className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-lg"
                                title="Start"
                              >
                                <Clock size={18} />
                              </button>
                            )}
                            {wo.status === 'in_progress' && isAdmin && (
                              <button
                                onClick={() => handleUpdateWorkOrder(wo.id, { status: 'completed' })}
                                className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-lg"
                                title="Complete"
                              >
                                <CheckCircle size={18} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        {/* Technicians Tab */}
        <TabsContent value="technicians">
          <div className="bg-white rounded-xl border border-[#E5E2D9] overflow-hidden">
            <div className="p-6 border-b border-[#E5E2D9] flex justify-between items-center">
              <h2 className="text-xl font-medium text-[#2B3210]">Technicians</h2>
              {isAdmin && (
                <button onClick={() => setOpenDialog('createTechnician')} className="btn-primary flex items-center gap-2" data-testid="create-technician-button">
                  <Plus size={18} /> Add Technician
                </button>
              )}
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#FBF8EF] border-b border-[#E5E2D9]">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase">Phone</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase">Specialization</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase">Open WOs</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-200">
                  {technicians.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-6 py-8 text-center text-[#505631]">
                        No technicians found. {isAdmin && 'Add technicians to enable auto-assignment.'}
                      </td>
                    </tr>
                  ) : (
                    technicians.map(tech => (
                      <tr key={tech.id} className="hover:bg-[#FBF8EF]">
                        <td className="px-6 py-4 text-sm font-medium text-[#2B3210]">{tech.name}</td>
                        <td className="px-6 py-4 text-sm text-[#505631]">{tech.phone}</td>
                        <td className="px-6 py-4">
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-[#E5E2D9] text-[#2B3210]">
                            {tech.specialization}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            tech.status === 'available' ? 'bg-emerald-100 text-emerald-800' :
                            tech.status === 'busy' ? 'bg-amber-100 text-amber-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {tech.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-[#2B3210]">{tech.open_work_orders || 0}</td>
                        <td className="px-6 py-4">
                          <div className="flex gap-2">
                            {isAdmin && (
                              <>
                                <button
                                  onClick={() => { setEditingItem(tech); setOpenDialog('editTechnician'); }}
                                  className="p-2 text-[#B2560D] hover:bg-orange-50 rounded-lg"
                                  title="Edit"
                                >
                                  <PencilSimple size={18} />
                                </button>
                                <button
                                  onClick={() => handleDeleteTechnician(tech.id)}
                                  className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                                  title="Delete"
                                >
                                  <Trash size={18} />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        {/* Preventive Maintenance Tab */}
        <TabsContent value="pm">
          <div className="bg-white rounded-xl border border-[#E5E2D9] overflow-hidden">
            <div className="p-6 border-b border-[#E5E2D9] flex justify-between items-center">
              <h2 className="text-xl font-medium text-[#2B3210]">Preventive Maintenance Schedules</h2>
              {isAdmin && (
                <button onClick={() => setOpenDialog('createPM')} className="btn-primary flex items-center gap-2" data-testid="create-pm-button">
                  <Plus size={18} /> New PM Schedule
                </button>
              )}
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#FBF8EF] border-b border-[#E5E2D9]">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase">Asset</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase">Frequency</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase">Next Due</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase">Last Completed</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-200">
                  {pmSchedules.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-6 py-8 text-center text-[#505631]">
                        No PM schedules found. {isAdmin && 'Create one to automate maintenance.'}
                      </td>
                    </tr>
                  ) : (
                    pmSchedules.map(pm => {
                      const today = new Date().toISOString().split('T')[0];
                      const isOverdue = pm.next_due_date < today;
                      return (
                        <tr 
                          key={pm.id} 
                          className={`hover:bg-[#FBF8EF] cursor-pointer transition-colors ${isOverdue ? 'bg-red-50' : ''}`}
                          onClick={() => { setSelectedPMSchedule(pm); setOpenDialog('pmDetails'); }}
                        >
                          <td className="px-6 py-4 text-sm font-medium text-[#2B3210]">{pm.name}</td>
                          <td className="px-6 py-4 text-sm text-[#505631]">{pm.asset_name}</td>
                          <td className="px-6 py-4">
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-[#E5E2D9] text-[#2B3210] capitalize">
                              {pm.frequency}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`text-sm ${isOverdue ? 'text-red-600 font-medium' : 'text-[#2B3210]'}`}>
                              {pm.next_due_date}
                              {isOverdue && <Warning size={14} className="inline ml-1" />}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm text-[#505631]">
                            {pm.last_completed ? new Date(pm.last_completed).toLocaleDateString() : 'Never'}
                          </td>
                          <td className="px-6 py-4" onClick={e => e.stopPropagation()}>
                            <button
                              onClick={() => handleCompletePM(pm.id)}
                              className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-lg"
                              title="Mark Complete"
                            >
                              <Check size={18} />
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        {/* Inventory Tab */}
        <TabsContent value="inventory">
          <div className="bg-white rounded-xl border border-[#E5E2D9] overflow-hidden">
            <div className="p-6 border-b border-[#E5E2D9] flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <h2 className="text-xl font-medium text-[#2B3210]">Parts Inventory</h2>
              <div className="flex flex-wrap gap-3 items-center">
                <div className="relative">
                  <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-[#505631]" size={18} />
                  <input
                    type="text"
                    placeholder="Search parts..."
                    value={partSearch}
                    onChange={(e) => setPartSearch(e.target.value)}
                    className="pl-10 pr-4 py-2 border border-[#E5E2D9] rounded-lg w-64"
                  />
                </div>
                {isAdmin && (
                  <button onClick={() => setOpenDialog('createPart')} className="btn-primary flex items-center gap-2" data-testid="create-part-button">
                    <Plus size={18} /> Add Part
                  </button>
                )}
              </div>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#FBF8EF] border-b border-[#E5E2D9]">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase">SKU</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase">Category</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase">Quantity</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase">Unit Cost</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-200">
                  {filteredParts.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="px-6 py-8 text-center text-[#505631]">
                        No parts found. {isAdmin && 'Add parts to track inventory.'}
                      </td>
                    </tr>
                  ) : (
                    filteredParts.map(part => (
                      <tr key={part.id} className="hover:bg-[#FBF8EF]">
                        <td className="px-6 py-4 text-sm font-mono text-[#505631]">{part.sku}</td>
                        <td className="px-6 py-4 text-sm font-medium text-[#2B3210]">{part.name}</td>
                        <td className="px-6 py-4 text-sm text-[#505631]">{part.category}</td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            {isAdmin && (
                              <button
                                onClick={() => handleAdjustStock(part.id, -1)}
                                className="w-6 h-6 rounded bg-[#E5E2D9] text-[#2B3210] hover:bg-[#D5D2C9] flex items-center justify-center"
                              >
                                -
                              </button>
                            )}
                            <span className={`text-sm font-medium ${part.quantity <= part.min_quantity ? 'text-red-600' : 'text-[#2B3210]'}`}>
                              {part.quantity}
                            </span>
                            {isAdmin && (
                              <button
                                onClick={() => handleAdjustStock(part.id, 1)}
                                className="w-6 h-6 rounded bg-[#E5E2D9] text-[#2B3210] hover:bg-[#D5D2C9] flex items-center justify-center"
                              >
                                +
                              </button>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-[#2B3210]">₦{part.unit_cost?.toLocaleString()}</td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(part.status)}`}>
                            {part.status.replace('_', ' ')}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          {isAdmin && (
                            <button
                              onClick={() => { setSelectedPart(part); setOpenDialog('editPart'); }}
                              className="p-2 text-[#B2560D] hover:bg-orange-50 rounded-lg"
                              title="Edit"
                            >
                              <PencilSimple size={18} />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Create Category Dialog */}
      <Dialog open={openDialog === 'createCategory'} onOpenChange={(open) => setOpenDialog(open ? 'createCategory' : null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Asset Category</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateCategory} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Category Name</label>
              <input type="text" name="name" required className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Description</label>
              <input type="text" name="description" className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg" />
            </div>
            <button type="submit" className="btn-primary w-full">Create Category</button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create Location Dialog */}
      <Dialog open={openDialog === 'createLocation'} onOpenChange={(open) => setOpenDialog(open ? 'createLocation' : null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Location</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateLocation} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Location Name</label>
              <input type="text" name="name" required className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Building</label>
                <input type="text" name="building" className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Floor</label>
                <input type="text" name="floor" className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Description</label>
              <input type="text" name="description" className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg" />
            </div>
            <button type="submit" className="btn-primary w-full">Create Location</button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create Asset Dialog */}
      <Dialog open={openDialog === 'createAsset'} onOpenChange={(open) => setOpenDialog(open ? 'createAsset' : null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New Asset</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateAsset} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Asset Name</label>
                <input type="text" name="name" required className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Asset Tag</label>
                <input type="text" name="asset_tag" required placeholder="e.g., HVAC-001" className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Category</label>
                <select name="category_id" required className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg">
                  <option value="">Select category...</option>
                  {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Location</label>
                <select name="location_id" required className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg">
                  <option value="">Select location...</option>
                  {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Purchase Date</label>
                <input type="date" name="purchase_date" className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Purchase Cost (₦)</label>
                <input type="number" name="purchase_cost" step="0.01" className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Serial Number</label>
                <input type="text" name="serial_number" className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Warranty Expiry</label>
                <input type="date" name="warranty_expiry" className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Notes</label>
              <textarea name="notes" rows="2" className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg" />
            </div>
            <button type="submit" className="btn-primary w-full">Create Asset</button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Asset Dialog */}
      <Dialog open={openDialog === 'editAsset'} onOpenChange={(open) => { setOpenDialog(open ? 'editAsset' : null); if (!open) setEditingItem(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Asset</DialogTitle>
          </DialogHeader>
          {editingItem && (
            <form onSubmit={handleUpdateAsset} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Asset Name</label>
                  <input type="text" name="name" defaultValue={editingItem.name} required className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Asset Tag</label>
                  <input type="text" name="asset_tag" defaultValue={editingItem.asset_tag} required className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Category</label>
                  <select name="category_id" defaultValue={editingItem.category_id} required className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg">
                    {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Location</label>
                  <select name="location_id" defaultValue={editingItem.location_id} required className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg">
                    {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Status</label>
                  <select name="status" defaultValue={editingItem.status} required className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg">
                    <option value="active">Active</option>
                    <option value="down">Down</option>
                    <option value="maintenance">Maintenance</option>
                    <option value="retired">Retired</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Purchase Cost (₦)</label>
                  <input type="number" name="purchase_cost" defaultValue={editingItem.purchase_cost} step="0.01" className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Serial Number</label>
                  <input type="text" name="serial_number" defaultValue={editingItem.serial_number} className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Warranty Expiry</label>
                  <input type="date" name="warranty_expiry" defaultValue={editingItem.warranty_expiry} className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Notes</label>
                <textarea name="notes" rows="2" defaultValue={editingItem.notes} className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg" />
              </div>
              <div className="flex gap-3">
                <button type="button" onClick={() => { setOpenDialog(null); setEditingItem(null); }} className="flex-1 px-4 py-2.5 border border-[#E5E2D9] rounded-lg">Cancel</button>
                <button type="submit" className="flex-1 btn-primary">Save Changes</button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Asset Details Dialog */}
      <Dialog open={openDialog === 'assetDetails'} onOpenChange={(open) => { setOpenDialog(open ? 'assetDetails' : null); if (!open) setSelectedAsset(null); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Asset Details</DialogTitle>
          </DialogHeader>
          {selectedAsset && (
            <div className="space-y-6">
              <div className="bg-[#FBF8EF] p-4 rounded-lg">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm text-[#505631]">Asset Tag</p>
                    <p className="text-xl font-mono font-bold text-[#B2560D]">{selectedAsset.asset_tag}</p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(selectedAsset.status)}`}>
                    {selectedAsset.status}
                  </span>
                </div>
                <h3 className="text-2xl font-semibold text-[#2B3210] mt-2">{selectedAsset.name}</h3>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-[#505631]">Category</p>
                  <p className="font-medium text-[#2B3210]">{selectedAsset.category_name}</p>
                </div>
                <div>
                  <p className="text-[#505631]">Location</p>
                  <p className="font-medium text-[#2B3210]">{selectedAsset.location_name}</p>
                </div>
                <div>
                  <p className="text-[#505631]">Serial Number</p>
                  <p className="font-medium text-[#2B3210]">{selectedAsset.serial_number || '-'}</p>
                </div>
                <div>
                  <p className="text-[#505631]">Purchase Cost</p>
                  <p className="font-medium text-[#2B3210]">{selectedAsset.purchase_cost ? `₦${selectedAsset.purchase_cost.toLocaleString()}` : '-'}</p>
                </div>
                <div>
                  <p className="text-[#505631]">Purchase Date</p>
                  <p className="font-medium text-[#2B3210]">{selectedAsset.purchase_date || '-'}</p>
                </div>
                <div>
                  <p className="text-[#505631]">Warranty Expiry</p>
                  <p className="font-medium text-[#2B3210]">{selectedAsset.warranty_expiry || '-'}</p>
                </div>
              </div>
              {selectedAsset.notes && (
                <div>
                  <p className="text-sm text-[#505631]">Notes</p>
                  <p className="text-sm text-[#2B3210] bg-[#FBF8EF] p-3 rounded-lg mt-1">{selectedAsset.notes}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Work Order Dialog */}
      <Dialog open={openDialog === 'createWorkOrder'} onOpenChange={(open) => setOpenDialog(open ? 'createWorkOrder' : null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Work Order</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateWorkOrder} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Title</label>
              <input type="text" name="title" required className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg" placeholder="Brief description of the issue" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Description</label>
              <textarea name="description" required rows="3" className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg" placeholder="Detailed description..." />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Asset (Optional)</label>
                <select name="asset_id" className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg">
                  <option value="">Select asset...</option>
                  {assets.map(a => <option key={a.id} value={a.id}>{a.asset_tag} - {a.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Priority</label>
                <select name="priority" required className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg">
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="critical">Critical</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Location (Optional)</label>
              <select name="location_id" className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg">
                <option value="">Select location...</option>
                {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </div>
            <button type="submit" className="btn-primary w-full">Create Work Order</button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Work Order Details Dialog */}
      <Dialog open={openDialog === 'workOrderDetails'} onOpenChange={(open) => { setOpenDialog(open ? 'workOrderDetails' : null); if (!open) setSelectedWorkOrder(null); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Work Order Details</DialogTitle>
          </DialogHeader>
          {selectedWorkOrder && (
            <div className="space-y-6">
              <div className="flex items-center gap-3">
                <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getPriorityColor(selectedWorkOrder.priority)}`}>
                  {selectedWorkOrder.priority}
                </span>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(selectedWorkOrder.status)}`}>
                  {selectedWorkOrder.status.replace('_', ' ')}
                </span>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-[#2B3210]">{selectedWorkOrder.title}</h3>
                <p className="text-sm text-[#505631] mt-2">{selectedWorkOrder.description}</p>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm bg-[#FBF8EF] p-4 rounded-lg">
                <div>
                  <p className="text-[#505631]">Asset</p>
                  <p className="font-medium text-[#2B3210]">{selectedWorkOrder.asset_name || '-'}</p>
                </div>
                <div>
                  <p className="text-[#505631]">Location</p>
                  <p className="font-medium text-[#2B3210]">{selectedWorkOrder.location_name || '-'}</p>
                </div>
                <div>
                  <p className="text-[#505631]">Requested By</p>
                  <p className="font-medium text-[#2B3210]">{selectedWorkOrder.requested_by_name}</p>
                </div>
                <div>
                  <p className="text-[#505631]">Assigned To</p>
                  <p className="font-medium text-[#2B3210]">{selectedWorkOrder.assigned_to_name || 'Unassigned'}</p>
                </div>
              </div>

              {/* Assigned Technician Section with WhatsApp */}
              {selectedWorkOrder.assigned_to && (
                <div className="bg-purple-50 border border-purple-200 p-4 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-purple-700">Assigned Technician</p>
                      <p className="font-semibold text-purple-900">{selectedWorkOrder.assigned_to_name}</p>
                      {selectedWorkOrder.assigned_to_phone && (
                        <p className="text-sm text-purple-600">{selectedWorkOrder.assigned_to_phone}</p>
                      )}
                    </div>
                    {selectedWorkOrder.assigned_to_phone && (
                      <button
                        onClick={() => openWhatsApp(selectedWorkOrder.assigned_to_phone, selectedWorkOrder)}
                        className="flex items-center gap-2 px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors"
                        data-testid="whatsapp-notify-btn"
                      >
                        <WhatsappLogo size={20} weight="fill" />
                        Notify via WhatsApp
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Reassignment Section (Admin only) */}
              {isAdmin && selectedWorkOrder.status !== 'completed' && selectedWorkOrder.status !== 'closed' && (
                <div className="border border-[#E5E2D9] p-4 rounded-lg">
                  <p className="text-sm font-medium text-[#2B3210] mb-2">Reassign to Technician</p>
                  <div className="flex gap-3">
                    <select
                      className="flex-1 px-3 py-2 border border-[#E5E2D9] rounded-lg text-sm"
                      defaultValue={selectedWorkOrder.assigned_to || ''}
                      onChange={(e) => {
                        if (e.target.value && e.target.value !== selectedWorkOrder.assigned_to) {
                          handleReassignWorkOrder(selectedWorkOrder.id, e.target.value);
                        }
                      }}
                    >
                      <option value="">Select technician...</option>
                      {technicians.filter(t => t.status === 'available').map(t => (
                        <option key={t.id} value={t.id}>
                          {t.name} - {t.specialization} ({t.open_work_orders || 0} open)
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {selectedWorkOrder.total_cost && (
                <div className="bg-[#B2560D] text-white p-4 rounded-lg">
                  <p className="text-sm">Total Cost</p>
                  <p className="text-2xl font-bold">₦{selectedWorkOrder.total_cost.toLocaleString()}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create PM Schedule Dialog */}
      <Dialog open={openDialog === 'createPM'} onOpenChange={(open) => setOpenDialog(open ? 'createPM' : null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create PM Schedule</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreatePMSchedule} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Schedule Name</label>
              <input type="text" name="name" required className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg" placeholder="e.g., Monthly HVAC Filter Change" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Asset</label>
              <select name="asset_id" required className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg">
                <option value="">Select asset...</option>
                {assets.map(a => <option key={a.id} value={a.id}>{a.asset_tag} - {a.name}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Frequency</label>
                <select name="frequency" required className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg">
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="yearly">Yearly</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Next Due Date</label>
                <input type="date" name="next_due_date" required className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Description</label>
              <textarea name="description" required rows="3" className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg" placeholder="What needs to be done..." />
            </div>
            <button type="submit" className="btn-primary w-full">Create Schedule</button>
          </form>
        </DialogContent>
      </Dialog>

      {/* PM Schedule Details Dialog */}
      <Dialog open={openDialog === 'pmDetails'} onOpenChange={(open) => { setOpenDialog(open ? 'pmDetails' : null); if (!open) setSelectedPMSchedule(null); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>PM Schedule Details</DialogTitle>
          </DialogHeader>
          {selectedPMSchedule && (
            <div className="space-y-6">
              {/* Status Badges */}
              <div className="flex items-center gap-3">
                <span className="px-3 py-1 rounded-full text-sm font-medium bg-[#E5E2D9] text-[#2B3210] capitalize">
                  {selectedPMSchedule.frequency}
                </span>
                {(() => {
                  const today = new Date().toISOString().split('T')[0];
                  const isOverdue = selectedPMSchedule.next_due_date < today;
                  return isOverdue ? (
                    <span className="px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800 flex items-center gap-1">
                      <Warning size={14} /> Overdue
                    </span>
                  ) : (
                    <span className="px-3 py-1 rounded-full text-sm font-medium bg-emerald-100 text-emerald-800">
                      On Schedule
                    </span>
                  );
                })()}
              </div>

              {/* Schedule Name and Description */}
              <div className="bg-[#FBF8EF] p-4 rounded-lg">
                <h3 className="text-xl font-semibold text-[#2B3210]">{selectedPMSchedule.name}</h3>
                <p className="text-sm text-[#505631] mt-2">{selectedPMSchedule.description}</p>
              </div>

              {/* Schedule Details Grid */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="bg-white border border-[#E5E2D9] p-4 rounded-lg">
                  <p className="text-[#505631]">Asset</p>
                  <p className="font-medium text-[#2B3210] text-lg">{selectedPMSchedule.asset_name || '-'}</p>
                </div>
                <div className="bg-white border border-[#E5E2D9] p-4 rounded-lg">
                  <p className="text-[#505631]">Frequency</p>
                  <p className="font-medium text-[#2B3210] text-lg capitalize">{selectedPMSchedule.frequency}</p>
                </div>
                <div className="bg-white border border-[#E5E2D9] p-4 rounded-lg">
                  <p className="text-[#505631]">Next Due Date</p>
                  <p className={`font-medium text-lg ${
                    selectedPMSchedule.next_due_date < new Date().toISOString().split('T')[0] 
                      ? 'text-red-600' 
                      : 'text-[#2B3210]'
                  }`}>
                    {selectedPMSchedule.next_due_date}
                  </p>
                </div>
                <div className="bg-white border border-[#E5E2D9] p-4 rounded-lg">
                  <p className="text-[#505631]">Last Completed</p>
                  <p className="font-medium text-[#2B3210] text-lg">
                    {selectedPMSchedule.last_completed 
                      ? new Date(selectedPMSchedule.last_completed).toLocaleDateString() 
                      : 'Never'}
                  </p>
                </div>
              </div>

              {/* Checklist if available */}
              {selectedPMSchedule.checklist && selectedPMSchedule.checklist.length > 0 && (
                <div className="border border-[#E5E2D9] p-4 rounded-lg">
                  <p className="text-sm font-medium text-[#2B3210] mb-3">Maintenance Checklist</p>
                  <ul className="space-y-2">
                    {selectedPMSchedule.checklist.map((item, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm text-[#505631]">
                        <div className="w-5 h-5 border border-[#E5E2D9] rounded flex items-center justify-center">
                          <Check size={12} className="text-[#505631]" />
                        </div>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Action Button */}
              {isAdmin && (
                <button
                  onClick={() => {
                    handleCompletePM(selectedPMSchedule.id);
                    setOpenDialog(null);
                    setSelectedPMSchedule(null);
                  }}
                  className="w-full btn-primary flex items-center justify-center gap-2"
                >
                  <CheckCircle size={20} />
                  Mark as Complete
                </button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Part Dialog */}
      <Dialog open={openDialog === 'createPart'} onOpenChange={(open) => setOpenDialog(open ? 'createPart' : null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Part to Inventory</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreatePart} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Part Name</label>
                <input type="text" name="name" required className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#2B3210] mb-1.5">SKU</label>
                <input type="text" name="sku" required className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg" placeholder="e.g., FILT-001" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Category</label>
              <input type="text" name="category" required className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg" placeholder="e.g., Filters, Electrical, Plumbing" />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Quantity</label>
                <input type="number" name="quantity" min="0" defaultValue="0" required className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Min Quantity</label>
                <input type="number" name="min_quantity" min="0" defaultValue="0" required className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Unit Cost (₦)</label>
                <input type="number" name="unit_cost" step="0.01" min="0" required className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Location</label>
                <input type="text" name="location" className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg" placeholder="e.g., Warehouse A" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Vendor</label>
                <input type="text" name="vendor" className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg" />
              </div>
            </div>
            <button type="submit" className="btn-primary w-full">Add Part</button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create Technician Dialog */}
      <Dialog open={openDialog === 'createTechnician'} onOpenChange={(open) => setOpenDialog(open ? 'createTechnician' : null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Technician</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateTechnician} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Full Name</label>
              <input type="text" name="name" required className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Phone Number (with country code)</label>
              <input type="text" name="phone" required placeholder="e.g., 2348012345678" className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg" />
              <p className="text-xs text-[#505631] mt-1">Include country code for WhatsApp notifications</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Specialization</label>
              <select name="specialization" required className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg">
                <option value="">Select specialization...</option>
                <option value="General">General</option>
                <option value="Electrical">Electrical</option>
                <option value="Plumbing">Plumbing</option>
                <option value="HVAC">HVAC</option>
                <option value="Carpentry">Carpentry</option>
                <option value="Painting">Painting</option>
                <option value="Cleaning">Cleaning</option>
                <option value="Landscaping">Landscaping</option>
                <option value="Security">Security</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Status</label>
              <select name="status" className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg">
                <option value="available">Available</option>
                <option value="busy">Busy</option>
                <option value="off_duty">Off Duty</option>
              </select>
            </div>
            <button type="submit" className="btn-primary w-full">Add Technician</button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Technician Dialog */}
      <Dialog open={openDialog === 'editTechnician'} onOpenChange={(open) => { setOpenDialog(open ? 'editTechnician' : null); if (!open) setEditingItem(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Technician</DialogTitle>
          </DialogHeader>
          {editingItem && (
            <form onSubmit={handleUpdateTechnician} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Full Name</label>
                <input type="text" name="name" defaultValue={editingItem.name} required className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Phone Number (with country code)</label>
                <input type="text" name="phone" defaultValue={editingItem.phone} required className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Specialization</label>
                <select name="specialization" defaultValue={editingItem.specialization} required className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg">
                  <option value="General">General</option>
                  <option value="Electrical">Electrical</option>
                  <option value="Plumbing">Plumbing</option>
                  <option value="HVAC">HVAC</option>
                  <option value="Carpentry">Carpentry</option>
                  <option value="Painting">Painting</option>
                  <option value="Cleaning">Cleaning</option>
                  <option value="Landscaping">Landscaping</option>
                  <option value="Security">Security</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Status</label>
                <select name="status" defaultValue={editingItem.status} className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg">
                  <option value="available">Available</option>
                  <option value="busy">Busy</option>
                  <option value="off_duty">Off Duty</option>
                </select>
              </div>
              <div className="flex gap-3">
                <button type="button" onClick={() => { setOpenDialog(null); setEditingItem(null); }} className="flex-1 px-4 py-2.5 border border-[#E5E2D9] rounded-lg">Cancel</button>
                <button type="submit" className="flex-1 btn-primary">Save Changes</button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Facilities;
